/**
 * SIL-1348 - all copy for the buyer listing-detail screen (spec section 4.2,
 * handoff section 2). Same discipline as `activeVerification.ts` and
 * `buyerVerification.ts`: a component in this directory renders layout and
 * imports its sentences from here, so product/legal copy has exactly one home.
 *
 * One remaining deliberate omission:
 *
 *  - **The unverified note stops after the verification sentence.** The
 *     handoff's second sentence ("Express interest is open to all registered
 *     buyers.") duplicates what the Express interest section (below) already
 *     says on its own, so it stays out of this note to avoid saying it twice.
 *
 * `minOfferUsd` has no label here either, and must not get one: it is the
 * seller's walk-away number, omitted from every buyer payload (SIL-1345).
 *
 * SIL-1429 (slice 4 child): "Express interest" labels now exist below,
 * where SIL-1348's header used to forbid them — that ticket's boundary was
 * "the string must not appear for either tier until slice 4"; this IS that
 * slice.
 */
import type { PaymentTerms } from '../../../dto/marketplace.js';
import { TIER_3_SELF_REPORTED_LABEL } from '../../../verification/presentation.js';
import { TECHNICAL_DETAIL_LABELS } from '../../../index.js';

// SIL-1381: re-exported for backward compatibility — TECHNICAL_DETAIL_LABELS
// moved to packages/shared/src/listing-technical-details.ts, beside the
// parser and the ListingTechnicalDetails type it labels, so the public
// exchange site (a later ticket) can import it without pulling in this
// customer-portal-only copy module.
export { TECHNICAL_DETAIL_LABELS };

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export const LISTING_BACK_LABEL = 'Browse Listings';
export const LISTING_ACCEPTING_OFFERS = 'Accepting offers';
export const LISTING_NOT_ACCEPTING_OFFERS = 'Not accepting offers';
export const listingIdEyebrow = (id: string) => `Listing ${id.slice(0, 8)}`;
export const listedOnLabel = (date: string) => `Listed ${date}`;

// ---------------------------------------------------------------------------
// Silicon Passport card
// ---------------------------------------------------------------------------

export const PASSPORT_CARD_EYEBROW = 'SILICON PASSPORT';
export const passportNodeCountLine = (nodes: number) =>
  `${nodes} node ${nodes === 1 ? 'Passport' : 'Passports'}`;

export const PASSPORT_AV_HEADING = 'Active Verification';
export const PASSPORT_FMV_HEADING = 'FMV band per GPU';
export const PASSPORT_FMV_LOW = 'Low';
export const PASSPORT_FMV_MEDIAN = 'Median';
export const PASSPORT_FMV_HIGH = 'High';
export const fmvCompsLine = (comps: number) =>
  `${comps} ${comps === 1 ? 'comparable' : 'comparables'}`;
export const PASSPORT_FMV_UNAVAILABLE = 'No FMV band for this SKU yet.';

/** The locked (unverified) body. The blur sits over FIXED DUMMY CONTENT — see
 * `PassportCard.tsx`. These are the overlay's own words. */
export const PASSPORT_LOCKED_HEADING = 'Available to verified buyers';
export const PASSPORT_LOCKED_BODY =
  'Active Verification results, chain of custody and the FMV band are shared under the platform NDA.';

/**
 * SIL-1627: every placeholder value is this one non-numeric mask, never a
 * dummy figure. The old dummies ("$0", "0/0", "—") were read by the customer
 * smoke run and by screen-scrapers as the listing's REAL values (FMV median
 * "$0", tier "-"). A mask cannot be mistaken for data and stays consistent
 * with the range-only unverified-buyer policy.
 */
export const PASSPORT_REDACTED_MASK = '•••';

/**
 * The placeholder the blur renders over. Fixed strings, identical for every
 * listing and every viewer: a CSS blur is a visual effect, not a redaction, so
 * the bytes underneath it must carry no information about THIS listing. Any
 * viewer who disables the filter, reads the accessibility tree or views source
 * sees only this.
 */
export const PASSPORT_PLACEHOLDER_ROWS: readonly { readonly label: string; readonly value: string }[] = [
  { label: 'Active Verification', value: PASSPORT_REDACTED_MASK },
  { label: 'Evidence tier', value: PASSPORT_REDACTED_MASK },
  { label: 'Run date', value: PASSPORT_REDACTED_MASK },
  { label: 'FMV median', value: PASSPORT_REDACTED_MASK },
  { label: 'Recorded events', value: PASSPORT_REDACTED_MASK },
  { label: 'Owners', value: PASSPORT_REDACTED_MASK },
] as const;

// ---------------------------------------------------------------------------
// Lot contents
// ---------------------------------------------------------------------------

export const LOT_CONTENTS_EYEBROW = 'LOT CONTENTS';
export const LOT_CONTENTS_TITLE = 'Assets in this listing';
export const lotContentsSubtitle = (nodes: number, gpus: number | null) => {
  const nodePart = `${nodes} ${nodes === 1 ? 'node' : 'nodes'}`;
  const gpuPart = gpus === null ? null : `${gpus} ${gpus === 1 ? 'GPU' : 'GPUs'}`;
  return [nodePart, gpuPart, 'each node carries its own Silicon Passport'].filter(Boolean).join(' · ');
};
export const LOT_CONTENTS_EMPTY = 'No assets in this lot.';
export const LOT_CONTENTS_LOCKED =
  'Node serials, per-node verification results and Passports are listed for verified buyers.';

export const LOT_TABLE_COLUMNS = {
  node: 'Node',
  passport: 'Passport',
  chassisSerial: 'Chassis S/N',
  activeVerification: 'Active Verification',
  runDate: 'Run date',
  custody: 'Custody',
} as const;
export const LOT_TABLE_PASSPORT_ACTION = 'Passport';
export const lotCustodyEvents = (events: number) =>
  `${events} ${events === 1 ? 'event' : 'events'}`;

// ---------------------------------------------------------------------------
// Active Verification state — the THREE-valued chip (SIL-1345)
// ---------------------------------------------------------------------------

/**
 * 'expired' and 'none' are BOTH the neutral third state: no current Active
 * Verification. Neither is a failure and neither may render the Verified mark,
 * so each gets its own plainly-descriptive label and the renderer gives them a
 * muted tone (never the red a FAIL uses). A Tier 3 run, even a current one,
 * also stays off the Verified mark — its label is the verbatim
 * `TIER_3_SELF_REPORTED_LABEL` owned by the shared presentation module.
 */
export const AV_STATE_LABELS: Record<'active' | 'expired' | 'none', string> = {
  active: 'Active Verification',
  expired: 'Active Verification expired',
  none: 'No Active Verification',
};

export const AV_STATE_NOTES: Record<'expired' | 'none', string> = {
  expired: 'The most recent run has aged out of its validity window. This is not a failed run.',
  none: 'No run has completed for the nodes behind this listing. This is not a failed run.',
};

export const AV_TIER_3_LABEL = TIER_3_SELF_REPORTED_LABEL;

export const avRunMeta = (tier: number | null, runDate: string, validTo: string) =>
  [tier === null ? null : `Tier ${tier}`, `run ${runDate}`, `valid to ${validTo}`]
    .filter(Boolean)
    .join(' · ');

export const avPassRatio = (pass: number, nodes: number) => `${pass}/${nodes}`;
export const AV_PASS_RATIO_CAPTION = 'nodes passed in the current window';

// ---------------------------------------------------------------------------
// Technical details
// ---------------------------------------------------------------------------

export const TECHNICAL_DETAILS_TITLE = 'Technical details';

export const gpusPerNodeValue = (gpus: number) => `${gpus}× GPU per node`;
export const gigabytesValue = (gb: number) => `${gb.toLocaleString('en-US')} GB`;

// ---------------------------------------------------------------------------
// Commercial terms
// ---------------------------------------------------------------------------

export const COMMERCIAL_TERMS_TITLE = 'Commercial terms';
export const COMMERCIAL_TERM_LABELS = {
  pricePerGpu: 'Price / GPU',
  pricePerNode: 'Price / node',
  soldAs: 'Sold as',
  location: 'Location',
  ships: 'Ships',
  payment: 'Payment',
  condition: 'Condition',
  available: 'Available',
} as const;

export const SOLD_AS_LABELS: Record<string, string> = {
  asset: 'Single node',
  lot: 'Lot',
  sku: 'Quantity of a SKU',
};

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  escrow: 'Escrow',
  wire_on_acceptance: 'Wire on acceptance',
  net_30: 'Net 30',
};

export const shipsWithinValue = (days: number) =>
  `Within ${days} ${days === 1 ? 'day' : 'days'}`;

// ---------------------------------------------------------------------------
// Action card
// ---------------------------------------------------------------------------

export const ACTION_LIST_PRICE_EYEBROW = 'LIST PRICE';
export const ACTION_RANGE_EYEBROW = 'INDICATIVE PRICE RANGE';
export const ACTION_PER_GPU_SUFFIX = '/ GPU';
export const ACTION_ROW_LABELS = {
  perNode: 'Per node',
  quantity: 'Quantity',
  lotTotal: 'Lot total',
} as const;
export const actionQuantityValue = (nodes: number) =>
  `${nodes} ${nodes === 1 ? 'node' : 'nodes'}`;

export const ACTION_BUY_NOW = 'Buy now';
export const ACTION_MAKE_OFFER = 'Make an offer';
export const ACTION_OFFERS_CLOSED = 'This listing is no longer accepting offers.';

/**
 * SIL-1472: an offers-only listing (`listing.offersOnly`) never shows Buy
 * now, for either buyer tier — Make an offer is the only action. A verified
 * buyer sees the Make an offer button directly (same range-tile layout as
 * the unverified price display, since a band listing shows the same band to
 * both tiers); an unverified buyer still needs to verify first, same as any
 * other offer today (SIL-1326), via `ACTION_VERIFY_TO_OFFER_*` below.
 */
export const ACTION_VERIFY_TO_OFFER_HEADING = 'Verify to make an offer';
export const ACTION_VERIFY_TO_OFFER_BODY =
  'Verify your entity once to make an offer on this listing.';

/**
 * SIL-1427 (spec section 5): `purchasePending` — another open buy_now
 * request already exists on this listing. Buy now is disabled with this
 * caption; Make an offer stays enabled (the second buyer can still offer).
 */
export const ACTION_PURCHASE_PENDING_CAPTION = 'Purchase pending';

// ---------------------------------------------------------------------------
// Buy now modal (SIL-1427, spec section 5 / handoff section 6)
// ---------------------------------------------------------------------------

export const BUY_NOW_MODAL_TITLE = 'Buy now';
export const BUY_NOW_ROW_LABELS = {
  quantity: 'Quantity',
  pricePerGpu: 'Price per GPU',
  pricePerNode: 'Price per node',
  total: 'Total',
} as const;

/** Verbatim copy from the issue — do not paraphrase. */
export const BUY_NOW_TERMS_NOTE =
  'The seller confirms availability within 48 hours. You then accept the purchase terms and pay the deposit. The Silicon Passport transfers to your entity at settlement.';

export const BUY_NOW_CONFIRM_LABEL = 'Confirm purchase request';
export const BUY_NOW_CONFIRM_PENDING_LABEL = 'Sending…';
export const BUY_NOW_SUCCESS_TOAST = 'Purchase request sent — the seller has 48 hours to confirm.';

// ---------------------------------------------------------------------------
// Make an offer modal (SIL-1427, spec section 5 / handoff section 6)
// ---------------------------------------------------------------------------

export const MAKE_OFFER_MODAL_TITLE = 'Make an offer';
export const MAKE_OFFER_TILE_LABELS = {
  lotTotal: 'Lot total',
  vsListPrice: 'vs. list price',
  vsFmvMedian: 'vs. FMV median',
} as const;
export const MAKE_OFFER_AMOUNT_LABEL = 'Your offer (USD)';
// SIL-1427: no MAKE_OFFER_NOTE_LABEL/PLACEHOLDER — the spec's optional note
// has nowhere to persist (`offer` has no note column and this child ships
// no migration); removed rather than shipped as a field that silently
// discards what the buyer types. Tracked as its own follow-up ticket.
export const MAKE_OFFER_BINDING_NOTE = 'Offers are binding for 72 hours once the seller accepts.';
export const MAKE_OFFER_SUBMIT_LABEL = 'Submit offer';
export const MAKE_OFFER_SUBMIT_PENDING_LABEL = 'Submitting…';

/**
 * SIL-1427: prefill the offer amount at 97% of the list price, rounded to
 * the nearest $100 — the handoff's own starting point for a negotiation, not
 * a server-computed number. `null` when the list price itself is unknown
 * (should not happen for a verified-tier listing, where this dialog is the
 * only caller).
 */
export function offerPrefillUsd(listPriceUsd: number): number {
  return Math.round((listPriceUsd * 0.97) / 100) * 100;
}

/**
 * SIL-1472 (design spec section 4 item 7): on a band listing (`price_display
 * = 'band'`), the offer modal prefills at the BAND MIDPOINT, rounded to the
 * nearest $100 — not 97% of a list price, since a band listing's buyer
 * never receives one. `loUsd`/`hiUsd` are the listing's own
 * `priceRange.total` bounds (the stored band × node count), already
 * buyer-safe at both tiers.
 */
export function bandMidpointOfferPrefillUsd(loUsd: number, hiUsd: number): number {
  return Math.round((loUsd + hiUsd) / 2 / 100) * 100;
}

/** A signed percent vs. a reference amount, e.g. "-3.0% vs. list price". */
export function pctVsLabel(amountUsd: number, referenceUsd: number): string {
  if (referenceUsd === 0) return '—';
  const pct = ((amountUsd - referenceUsd) / referenceUsd) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Seller offers table — buy_now row labeling (SIL-1427)
// ---------------------------------------------------------------------------

export const BUY_NOW_REQUEST_LABEL = 'Buy now request';
export const ACTION_CONFIRM_AVAILABILITY = 'Confirm availability';
export const ACTION_DECLINE = 'Decline';

/**
 * Compact "time left" for a buy_now offer's 48h seller SLA
 * (`offer.expires_at`). A static label, not a ticking clock — this renders
 * inside a table row, not a countdown surface like `AuctionCountdown`.
 */
export function buyNowTimeLeftLabel(expiresAt: string | null): string {
  if (expiresAt == null) return '—';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

/** The delta badge beside the list price. `pct` is the server's `pctVsFmv`. */
export const pctVsFmvLabel = (pct: number): string => {
  const magnitude = `${Math.abs(pct).toFixed(1)}%`;
  if (Math.abs(pct) < 0.05) return 'At FMV median';
  return `${magnitude} ${pct < 0 ? 'below' : 'above'} FMV median`;
};

export const ACTION_UNLOCK_HEADING = 'Unlock the exact price';
export const ACTION_UNLOCK_BODY =
  'Verify your entity once to see the list price, buy, make offers and open every node Passport.';

/** See this module's header: deliberately one sentence, not the handoff's two. */
export const ACTION_UNVERIFIED_NOTE = 'Buy now and offers require buyer verification.';

// ---------------------------------------------------------------------------
// Express interest (SIL-1429, slice 4 child 6 of SIL-1319). Spec section 5;
// handoff docs/exchange/design-handoff/ section 6. Shown to BOTH buyer
// tiers — unlike Buy now / Make an offer, it needs no verification, only a
// business org (canBuy + organizationType === 'business'). Hidden for the
// listing's owner org and for individual orgs (the route 403s either way;
// this is presentation only, not the boundary).
// ---------------------------------------------------------------------------

export const EXPRESS_INTEREST_BUTTON = 'Express interest';
export const EXPRESS_INTEREST_EDIT_BUTTON = 'Edit your interest';
export const EXPRESS_INTEREST_MODAL_TITLE = 'Express interest';
export const EXPRESS_INTEREST_MODAL_SUBTITLE =
  'Share your workload with the seller. This is a non-binding signal — the seller never sees your company or contact details.';

export const EXPRESS_INTEREST_FIELD_LABELS = {
  companyCategory: 'Company category',
  useCase: 'Use case',
  timeline: 'Timeline',
  message: 'Message (optional)',
  /** Never call this a sourcing preference — see migration 973's header. */
  buyerRegion: 'Your region',
} as const;

export const EXPRESS_INTEREST_TIMELINE_LABELS: Record<string, string> = {
  now: 'Now',
  '1_3_months': '1–3 months',
  '3_6_months': '3–6 months',
  '6_plus_months': '6+ months',
};

export const EXPRESS_INTEREST_REGION_PLACEHOLDER = 'Select a region…';
export const EXPRESS_INTEREST_SELECT_PLACEHOLDER = 'Select…';
export const EXPRESS_INTEREST_SUBMIT = 'Send interest';
export const EXPRESS_INTEREST_SUBMITTING = 'Sending…';
export const EXPRESS_INTEREST_CANCEL = 'Cancel';
export const EXPRESS_INTEREST_SUCCESS_TOAST = 'Interest shared with the seller.';
export const EXPRESS_INTEREST_GENERIC_ERROR = 'Could not share your interest. Try again.';
/** 400 unknown_region — the live `region` table moved under a stale client
 * list; surfaced on the field, not a generic toast (see the PUT handler,
 * apps/api/src/routes/client/listings.ts). */
export const EXPRESS_INTEREST_UNKNOWN_REGION_ERROR =
  'This region is no longer available. Pick another.';

/** Owner-only, read-only — spec section 5 "minimal for this slice": no
 * reply control, slice 5 (SIL-1320) moves this into My Listings. */
export const SELLER_INTEREST_LIST_TITLE = 'Expressed interest';
export const SELLER_INTEREST_LIST_EMPTY = 'No expressed interest yet.';

/** SIL-1471: seller-private notes on the owner's own listing (spec section
 * 8). Never rendered for a non-owner — notes carry no public/buyer meaning
 * at all, unlike the expressed-interest panel above. */
export const LISTING_NOTES_TITLE = 'Notes';
export const LISTING_NOTES_SUBTITLE = 'Only visible to your organization.';
export const LISTING_NOTES_EMPTY = 'No notes yet.';
export const LISTING_NOTES_PLACEHOLDER = 'Add a note…';
export const LISTING_NOTES_ADD_BUTTON = 'Add note';
export const LISTING_NOTES_SAVE_BUTTON = 'Save';
export const LISTING_NOTES_CANCEL_BUTTON = 'Cancel';
export const LISTING_NOTES_EDIT_BUTTON = 'Edit';
export const LISTING_NOTES_DELETE_BUTTON = 'Delete';
export const LISTING_NOTES_DELETE_CONFIRM = 'Delete this note?';
